if (!window.CartCrossSellCore) {
  window.CartCrossSellCore = class CartCrossSellCore {
    constructor(component, options = {}) {
      // 保存组件级交互配置；tracking 由单卡片和多卡片组件分别传入。
      this.component = component;
      this.source = options.source || 'cart-cross-sell';
      this.refreshTargetPrefix = options.refreshTargetPrefix || 'CartCrossSell-';
      this.tracking = options.tracking || null;
      this.replaceCartRoots = options.replaceCartRoots || false;

      // 埋点生命周期状态：防止重复绑定，并统一管理曝光计时器。
      this.trackingConnected = false;
      this.trackingObserver = undefined;
      this.trackingTimers = new Map();
      this.trackingClickHandler = this.handleTrackingClick.bind(this);
    }

    connectTracking() {
      if (!this.tracking || this.trackingConnected) return;

      // 使用组件级事件代理识别商品链接点击，避免为每张卡片重复绑定。
      this.trackingConnected = true;
      this.component.addEventListener('click', this.trackingClickHandler);

      // 不支持 IntersectionObserver 时跳过曝光埋点，但保留点击和成功加购埋点。
      if (!('IntersectionObserver' in window)) return;

      // 卡片至少 50% 可见后开始 500ms 有效曝光计时。
      this.trackingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              this.scheduleTrackingView(entry.target);
            } else {
              this.clearTrackingTimer(entry.target);
            }
          });
        },
        {
          threshold: [0, 0.5],
        }
      );

      this.getTrackingCards().forEach((card) => {
        this.trackingObserver.observe(card);
      });
    }

    disconnectTracking() {
      if (!this.trackingConnected) return;

      // 组件被购物车局部刷新替换时，释放监听器、Observer 和未完成计时器。
      this.component.removeEventListener('click', this.trackingClickHandler);
      this.trackingObserver?.disconnect();
      this.trackingObserver = undefined;

      this.trackingTimers.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      this.trackingTimers.clear();
      this.trackingConnected = false;
    }

    getTrackingCards() {
      if (!this.tracking?.cardSelector) return [];

      return Array.from(this.component.querySelectorAll(this.tracking.cardSelector));
    }

    getTrackingCard(element) {
      if (!(element instanceof Element) || !this.tracking?.cardSelector) {
        return null;
      }

      const card = element.matches(this.tracking.cardSelector)
        ? element
        : element.closest(this.tracking.cardSelector);

      if (!card || !this.component.contains(card)) return null;

      return card;
    }

    getTrackingItem(element) {
      const card = this.getTrackingCard(element);
      if (!card) return null;

      // 直接复用现有加购按钮的变体 ID，避免从展示文案或 URL 推测商品身份。
      const button = card.querySelector('[data-cart-cross-sell-submit]');
      const itemId = button?.dataset.variantId;
      const recommendationRuleId = card.dataset.recommendationRuleId;
      const configuredSlot = card.dataset.configuredSlot;

      if (!itemId) return null;

      // 标准字段描述商品与推荐列表；卡片额外携带稳定的后台规则和配置位。
      const title = card.querySelector(this.tracking.titleSelector);
      const item = {
        item_id: String(itemId),
        item_list_id: this.tracking.itemListId,
        item_list_name: this.tracking.itemListName,
        item_brand: 'WonderBiotics',
        quantity: 1,
      };

      /*
        id: cart_cross_sell_tracking_title
        source: card.dataset.productTitle
        fallback: visible title text
        purpose: preserve product identity when display titles are customized
      */
      const itemName =
        card.dataset.productTitle?.trim() || title?.textContent?.trim();
      if (itemName) {
        item.item_name = itemName;
      }

      if (recommendationRuleId) {
        item.recommendation_rule_id = recommendationRuleId;
      }

      if (configuredSlot) {
        item.configured_slot = configuredSlot;
      }

      return item;
    }

    getTrackingState() {
      // 跨组件和局部刷新保存页面级曝光记录，避免同列表同商品重复上报。
      if (
        !window.CartCrossSellTrackingState ||
        !(window.CartCrossSellTrackingState.viewedItems instanceof Set)
      ) {
        window.CartCrossSellTrackingState = {
          viewedItems: new Set(),
        };
      }

      return window.CartCrossSellTrackingState;
    }

    getTrackingViewKey(item) {
      return `${item.item_list_id}:${item.item_id}`;
    }

    scheduleTrackingView(card) {
      const item = this.getTrackingItem(card);
      if (!item || this.trackingTimers.has(card)) return;

      const trackingState = this.getTrackingState();
      // 列表 ID 与商品 ID 共同组成去重键，允许同一商品在不同推荐列表分别归因。
      const viewKey = this.getTrackingViewKey(item);

      if (trackingState.viewedItems.has(viewKey)) return;

      const timerId = window.setTimeout(() => {
        this.trackingTimers.delete(card);

        // 计时结束时组件可能已被购物车局部刷新替换，断开节点不发送曝光。
        if (!card.isConnected) return;

        const currentItem = this.getTrackingItem(card);
        if (!currentItem) return;

        const currentViewKey = this.getTrackingViewKey(currentItem);
        if (trackingState.viewedItems.has(currentViewKey)) return;

        trackingState.viewedItems.add(currentViewKey);
        this.pushTrackingEvent('cart_cross_sell_view', currentItem);
      }, 500);

      this.trackingTimers.set(card, timerId);
    }

    clearTrackingTimer(card) {
      const timerId = this.trackingTimers.get(card);
      if (timerId === undefined) return;

      window.clearTimeout(timerId);
      this.trackingTimers.delete(card);
    }

    handleTrackingClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      const productLink = target?.closest('a[href]');

      // 仅商品链接发送 select；关联销售 ADD 按钮由成功加购分支单独处理。
      if (!productLink || !this.component.contains(productLink)) return;

      const item = this.getTrackingItem(productLink);
      if (!item) return;

      this.pushTrackingEvent('cart_cross_sell_select', item);
    }

    trackSuccessfulAdd(item) {
      if (!item) return;

      // 该方法只由 Shopify Ajax 加购成功分支调用，失败请求不会产生归因事件。
      this.pushTrackingAddEvent(item);
    }

    pushTrackingAddEvent(item) {
      if (!item) return;

      try {
        window.dataLayer = window.dataLayer || [];

        // 防止 GTM Data Layer Model 继承上一条电商事件。
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'cart_cross_sell_add',
          variant_id: item.item_id,
          item_name: item.item_name || '',
          item_list_id: item.item_list_id || '',
          item_list_name: item.item_list_name || '',
          quantity: item.quantity || 1,
          recommendation_rule_id: item.recommendation_rule_id || '',
          configured_slot: item.configured_slot || '',
        });
      } catch (error) {
        // 埋点异常必须 fail open，不能阻断加购或购物车刷新。
        console.error('Cart cross-sell add tracking failed.', error);
      }
    }

    pushTrackingEvent(eventName, item) {
      if (!eventName || !item) return;

      try {
        window.dataLayer = window.dataLayer || [];

        // 先清空旧 ecommerce 对象，防止 GTM 合并上一次事件的商品字段。
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: eventName,
          ecommerce: {
            items: [item],
          },
        });
      } catch (error) {
        // 埋点异常必须 fail open，不能阻断商品跳转、加购或购物车刷新。
        console.error('Cart cross-sell tracking failed.', error);
      }
    }

    getRefreshTargetId() {
      return this.component.dataset.refreshTargetId || `${this.refreshTargetPrefix}${this.component.dataset.sectionId}`;
    }

    async refresh() {
      // 购物车发生其他更新时，重新请求当前 section，刷新推荐规则和商品状态。
      const wrapper = this.component.closest('[data-cart-cross-sell-wrapper]');
      const sectionId = this.component.dataset.sectionId;
      const refreshTargetId = this.getRefreshTargetId();

      if (!wrapper || !sectionId || !refreshTargetId) return;

      try {
        const response = await fetch(`${window.location.pathname || '/cart'}?section_id=${sectionId}`);
        if (!response.ok) return;

        const html = await response.text();
        const sourceElement = this.getSectionElement(html, `#${refreshTargetId}`);
        if (!sourceElement) return;

        wrapper.replaceWith(sourceElement);
      } catch (error) {
        console.error(error);
      }
    }

    async addRecommendationToCart(variantId) {
      // 使用 Shopify Ajax Cart，并同时请求需要局部刷新的购物车 sections。
      const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
      const sectionIds = this.getSectionsToRender()
        .map((section) => section.section)
        .filter(Boolean)
        .filter((sectionId, index, allSectionIds) => allSectionIds.indexOf(sectionId) === index);

      if (!sectionIds.length) {
        throw new Error('No sections available for cart cross-sell refresh.');
      }

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname || '/cart');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(cartAddUrl, config);

      if (!response.ok) {
        throw new Error(`Cart add request failed with status ${response.status}`);
      }

      return response.json();
    }

    getSectionsToRender() {
      // 返回关联销售加购成功后需要同步刷新的购物车区域。
      const mainCartItems = document.getElementById('main-cart-items');
      const mainCartFooter = document.getElementById('main-cart-footer');

      // 空购物车加购会同时改变 warnings、商品列表和 footer 结算区，必须替换两个 section 根节点。
      if (this.replaceCartRoots) {
        return [
          {
            id: 'main-cart-items-root',
            section: mainCartItems?.dataset.id || 'main-cart-items',
            selector: 'cart-items',
            currentSelector: 'cart-items',
            replace: true,
          },
          {
            id: 'main-cart-footer',
            section: mainCartFooter?.dataset.id || 'main-cart-footer',
            selector: '#main-cart-footer',
            replace: true,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }

      const sections = [
        {
          id: 'main-cart-items',
          section: mainCartItems?.dataset.id || 'main-cart-items',
          selector: '.js-contents',
        },
        {
          id: 'main-cart-footer',
          section: mainCartFooter?.dataset.id || 'main-cart-footer',
          selector: '.js-contents',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];

      if (this.component.dataset.sectionId) {
        const refreshTargetId = this.getRefreshTargetId();
        sections.push({
          id: refreshTargetId,
          section: this.component.dataset.sectionId,
          selector: `#${refreshTargetId}`,
          replace: true,
        });
      }

      return sections;
    }

    renderSections(sections) {
      if (!sections || typeof sections !== 'object') return;

      // 使用 Ajax 响应中的 section HTML 局部更新页面，避免整页跳转。
      this.getSectionsToRender().forEach((section) => {
        const html = sections[section.section];
        if (!html) return;

        const currentElement = section.currentSelector
          ? document.querySelector(section.currentSelector)
          : section.id === 'cart-icon-bubble'
            ? document.getElementById('cart-icon-bubble')
            : document.getElementById(section.id);

        if (!currentElement) return;

        const sourceElement = this.getSectionElement(html, section.selector);
        if (!sourceElement) return;

        if (section.replace) {
          currentElement.replaceWith(sourceElement);
          return;
        }

        const targetElement = section.selector
          ? currentElement.querySelector(section.selector) || currentElement
          : currentElement;

        if (section.id === 'cart-icon-bubble') {
          currentElement.innerHTML = sourceElement.innerHTML;
        } else {
          targetElement.innerHTML = sourceElement.innerHTML;
        }
      });
    }

    getSectionElement(html, selector) {
      if (!html || !selector) return null;

      // 将 Shopify 返回的 section HTML 转为可替换的 DOM 节点。
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    publishCartUpdate(parsedState, variantId) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      // 通知 Dawn 购物车订阅者刷新状态，并携带来源与实际加入的变体 ID。
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: this.source,
        cartData: parsedState,
        variantId,
      });
    }
  };
}
